<script setup lang="ts">
import PlusMenuItem from '@layout/nav-v2/mobile/components/PlusMenuItem.vue';
import PlusMenuLink from '@layout/nav-v2/mobile/components/PlusMenuLink.vue';
import MenuList from '@layout/nav-v2/mobile/components/MenuList.vue';
import HelpMenuSubmenuItem from '@layout/nav-v2/mobile/components/menus/HelpMenuSubmenuItem.vue';
import Icon from '@components/Icon.vue';
import IconCaretDown from '@icons/ph_caret-down.svg';
import IconQuestionCircle from '@icons/ph_question-circle.svg';
import IconWhatsapp from '@icons/ph_whatsapp_gray-darker.svg';
import IconPhoneInbound from '@icons/ph_phone-inbound.svg';
import IconEnvelope from '@icons/ph_envelope.svg';
import Typography from '@components/Typography.vue';
import { trans } from '@elements/translation.ts';
import { ref } from 'vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import { URL_CONTACT } from '@tools/routes/routes';
import { storeToRefs } from 'pinia';

const navbarStore = useNavbarStore();
const { isCurrentPage } = navbarStore;
const { whatsappLink, recalledSubmenus, faqLink } = storeToRefs(navbarStore);

const isOpen = ref<boolean>(false);

const toggleDropdown = (): void => {
  isOpen.value = !isOpen.value;
};
</script>

<template>
  <div>
    <PlusMenuItem @click="toggleDropdown">
      <template #icon>
        <Icon
          :svg="IconQuestionCircle"
          width="24"
          height="24"
          :fill="isOpen ? 'black' : 'gray-darker'"
        />
      </template>

      <template #text>
        <div class="flex items-center justify-between">
          <Typography
            :variant="isOpen ? 'body-bold' : 'body'"
            class="whitespace-nowrap"
          >
            {{ trans('user_menu.help') }}
          </Typography>

          <Icon
            :svg="IconCaretDown"
            height="24"
            width="24"
            fill="gray-darker"
            :class="[
              'transition-transform',
              'duration-200',
              { 'rotate-180': isOpen },
            ]"
          />
        </div>
      </template>
    </PlusMenuItem>

    <MenuList
      v-if="isOpen"
      class="flex flex-col space-y-1"
    >
      <li v-if="whatsappLink">
        <PlusMenuLink :link="whatsappLink">
          <template #icon="{ isActive }">
            <Icon
              :svg="IconWhatsapp"
              width="24"
              height="24"
              :fill="isActive ? 'black' : 'gray-darker'"
            />
          </template>

          <template #text>
            {{ trans('user_menu.whatsapp') }}
          </template>
        </PlusMenuLink>
      </li>

      <li v-if="recalledSubmenus.length > 0">
        <PlusMenuItem>
          <template #icon>
            <Icon
              :svg="IconPhoneInbound"
              width="24"
              height="24"
              stroke="gray-darker"
              has-icon-stroke
            />
          </template>

          <template #text>
            {{ trans('user_menu.recalled') }}
          </template>
        </PlusMenuItem>
      </li>

      <li v-if="recalledSubmenus.length > 0">
        <MenuList
          class="help-menu-dropdown__submenus"
          :class="[
            'ml-4',
            'px-2 pr-0',
            'flex flex-col',
          ]"
        >
          <li
            v-for="submenu in recalledSubmenus"
            :key="submenu.name"
          >
            <HelpMenuSubmenuItem
              v-if="submenu.link"
              :link="submenu.link"
              :is-active="isCurrentPage(submenu.link)"
            >
              {{ submenu.name }}
            </HelpMenuSubmenuItem>
          </li>
        </MenuList>
      </li>

      <li v-if="faqLink">
        <PlusMenuLink :link="faqLink">
          <template #icon="{ isActive }">
            <Icon
              :svg="IconQuestionCircle"
              width="24"
              height="24"
              :fill="isActive ? 'black' : 'gray-darker'"
            />
          </template>

          <template #text>
            {{ trans('user_menu.faq') }}
          </template>
        </PlusMenuLink>
      </li>

      <li>
        <PlusMenuLink :link="URL_CONTACT">
          <template #icon="{ isActive }">
            <Icon
              :svg="IconEnvelope"
              width="24"
              height="24"
              :fill="isActive ? 'black' : 'gray-darker'"
            />
          </template>

          <template #text>
            {{ trans('user_menu.send_message') }}
          </template>
        </PlusMenuLink>
      </li>
    </MenuList>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables/colors' as *;
@use '@styles/mixins/rem' as *;

.help-menu-dropdown {
  &__submenus {
    border-left: toRem(2) solid $colorGray;
  }
}
</style>
